import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Baby, Syringe, Calendar, CheckCircle2, ExternalLink } from 'lucide-react';

const schedule = [
  { age: '5 uger', type: 'Børneundersøgelse', icon: Baby, color: 'bg-blue-50 text-blue-600' },
  { age: '3 måneder', type: 'Kun vaccination', icon: Syringe, color: 'bg-teal-50 text-teal-600' },
  { age: '5 måneder', type: 'Børneundersøgelse + vaccination', icon: Calendar, color: 'bg-indigo-50 text-indigo-600' },
  { age: '12 måneder', type: 'Børneundersøgelse + vaccination', icon: Calendar, color: 'bg-indigo-50 text-indigo-600' },
  { age: '15 måneder', type: 'Kun vaccination', icon: Syringe, color: 'bg-teal-50 text-teal-600' },
  { age: '2 år', type: 'Børneundersøgelse', icon: Baby, color: 'bg-blue-50 text-blue-600' },
  { age: '3 år', type: 'Børneundersøgelse', icon: Baby, color: 'bg-blue-50 text-blue-600' },
  { age: '4 år', type: 'Børneundersøgelse + vaccination', icon: Calendar, color: 'bg-indigo-50 text-indigo-600' },
  { age: '5 år', type: 'Børneundersøgelse + vaccination', icon: Calendar, color: 'bg-indigo-50 text-indigo-600' },
  { age: '12 år', type: 'Vaccination', icon: Syringe, color: 'bg-teal-50 text-teal-600' },
];

export default function Boerneundersoegelser() {
  return (
    <div className="bg-white min-h-screen">
      <Helmet>
        <title>Børneundersøgelser og Vaccinationer | Lægehuset</title>
        <meta name="description" content="Information om børneundersøgelser og vaccinationer hos vores jordemoder." />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-slate-50 pt-20 pb-12 md:pt-24 md:pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-left">
            <h1 className="text-4xl md:text-5xl font-sans font-medium text-black tracking-tight mb-6">
              Børneundersøgelser og vaccinationer
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl leading-relaxed">
              Vi glæder os til at følge dit barns udvikling. Børneundersøgelserne varetages af vores jordemoder og sikrer en tryg start på livet.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-white border border-slate-100 shadow-sm overflow-hidden">
            <div className="p-8 md:p-12">
              <div className="flex items-start gap-4 mb-12 p-6 bg-gray-50 border border-gray-200">
                <div className="text-slate-600">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-lg font-sans font-medium text-black mb-1">Vigtig information</h2>
                  <p className="text-slate-600">
                    Alle børneundersøgelser varetages hos vores jordemoder. Husk at medbringe barnets vaccinationskort og eventuelt den gule sundhedsbog.
                  </p>
                </div>
              </div>

              <h3 className="text-2xl font-sans font-medium text-black mb-8">Program for undersøgelser</h3>
              
              <div className="space-y-4">
                {schedule.map((item, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-4 md:p-6 border border-slate-100 hover:border-teal-200 hover:bg-teal-50/30 transition-all duration-200 group"
                  >
                    <div className="flex items-center gap-4 md:gap-6">
                      <div className="text-[#4b717a] group-hover:scale-110 transition-transform duration-200">
                        <item.icon className="w-6 h-6" />
                      </div>
                      <div>
                        <span className="block text-sm font-medium text-slate-500 mb-0.5">{item.age}</span>
                        <span className="block text-lg font-sans font-medium text-black">{item.type}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-12 pt-12 border-t border-slate-100">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6 bg-slate-50 p-8">
                  <div>
                    <h4 className="text-xl font-sans font-medium text-black mb-2">Det danske børnevaccinationsprogram</h4>
                    <p className="text-slate-600">
                      Du kan læse mere om de enkelte vacciner og formålet med programmet på Sundhedsstyrelsens hjemmeside.
                    </p>
                  </div>
                  <a 
                    href="https://www.sst.dk/da/viden/vaccination/boernevaccinationsprogrammet" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap"
                  >
                    Se programmet her
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
